import type { GeoJsonGeometry, MapObject, ObjectLocalized, VectorFeature } from "@fortex/api";
import type { CatalogueEntry } from "../catalogue/types";
import { numericId } from "../core/support";
import { geometryPoints, geometryWkt, parseWkt } from "../geo/wkt";
import type { WktGeometry } from "../geo/wkt";
import { styleFromVector } from "./style";
import type { Feature, FeatureCluster, LatLngTuple } from "./types";

export function featureKey(layerId: string, id: string): string {
  return `${layerId}|${id}`;
}

function position(coordinate: number[]): LatLngTuple {
  return [coordinate[1], coordinate[0]];
}

export function geometryFromGeoJson(geometry: GeoJsonGeometry | null | undefined): WktGeometry | null {
  if (!geometry) return null;

  switch (geometry.type) {
    case "Point":
      return geometry.coordinates.length >= 2 ? { kind: "point", point: position(geometry.coordinates) } : null;
    case "MultiPoint":
      return multi(geometry.coordinates.map(a => ({ kind: "point", point: position(a) }) as WktGeometry));
    case "LineString":
      return { kind: "line", points: geometry.coordinates.map(position) };
    case "MultiLineString":
      return multi(geometry.coordinates.map(a => ({ kind: "line", points: a.map(position) }) as WktGeometry));
    case "Polygon":
      return { kind: "polygon", rings: geometry.coordinates.map(ring => ring.map(position)) };
    case "MultiPolygon":
      return multi(geometry.coordinates.map(a => ({ kind: "polygon", rings: a.map(ring => ring.map(position)) }) as WktGeometry));
    case "GeometryCollection":
      return multi(geometry.geometries.map(geometryFromGeoJson).filter((a): a is WktGeometry => a !== null));
  }
}

function multi(parts: WktGeometry[]): WktGeometry | null {
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0];

  return { kind: "multi", parts };
}

export function anchorOf(geometry: WktGeometry | null, fallback: LatLngTuple | null): LatLngTuple {
  if (geometry === null) return fallback ?? [0, 0];
  if (geometry.kind === "point") return geometry.point;

  const points = geometryPoints(geometry);

  if (points.length === 0) return fallback ?? [0, 0];

  if (geometry.kind === "line") return points[Math.floor(points.length / 2)];

  let lat = 0;
  let lon = 0;

  for (const point of points) {
    lat += point[0];
    lon += point[1];
  }

  return [lat / points.length, lon / points.length];
}

export function objectName(object: MapObject): string {
  return object.name && object.name.trim().length > 0 ? object.name : (object.idName ?? `#${object.id}`);
}

export function clusterFromObject(object: MapObject): FeatureCluster | null {
  if (object.memberCount <= 1) return null;

  return { count: object.memberCount, memberIds: object.memberIds.map(String), typeCounts: { ...(object.memberTypeCounts ?? {}) } };
}

export function featureFromMapObject(object: MapObject, layerId: string): Feature {
  const parsed = parseWkt(object.wkt);
  const point: LatLngTuple = [object.lat, object.lon];
  const geometry: WktGeometry = parsed ?? { kind: "point", point };

  return {
    key: featureKey(layerId, String(object.id)),
    id: String(object.id),
    layerId,
    geometry,
    anchor: object.lat !== 0 || object.lon !== 0 ? point : anchorOf(geometry, point),
    properties: { name: object.name, idName: object.idName, typeName: object.typeName },
    label: objectName(object),
    style: null,
    serverStyle: null,
    cluster: clusterFromObject(object),
    object,
    typeIdName: object.typeIdName
  };
}

function propertyText(properties: Record<string, unknown>, keys: string[]): string | null {
  const lower = new Map(Object.keys(properties).map(a => [a.toLowerCase(), a] as const));

  for (const key of keys) {
    const actual = lower.get(key.toLowerCase());

    if (actual === undefined) continue;

    const value = properties[actual];

    if (typeof value === "string" && value.trim().length > 0) return value;
    if (typeof value === "number") return String(value);
  }

  return null;
}

export function mapObjectFromFeature(feature: Feature): MapObject | null {
  if (feature.object) return feature.object;

  const id = numericId(feature.id);

  if (id === null) return null;

  const typeIdName = feature.typeIdName ?? propertyText(feature.properties, ["typeIdName", "type_IdName"]);

  return {
    id,
    idName: propertyText(feature.properties, ["idName"]),
    name: feature.label ?? propertyText(feature.properties, ["name"]),
    typeId: Number(propertyText(feature.properties, ["typeId", "type_Id"]) ?? 0),
    typeIdName,
    typeName: propertyText(feature.properties, ["typeName"]),
    geometryIdName: propertyText(feature.properties, ["geometryIdName"]),
    parentId: numericId(propertyText(feature.properties, ["parentId", "parent_Id"])),
    showOnMap: true,
    publishable: true,
    lat: feature.anchor[0],
    lon: feature.anchor[1],
    wkt: geometryWkt(feature.geometry),
    memberCount: feature.cluster?.count ?? 1,
    memberIds: feature.cluster ? feature.cluster.memberIds.map(Number).filter(Number.isFinite) : [id],
    memberTypeCounts: feature.cluster?.typeCounts ?? null
  };
}

export function featureFromVector(vector: VectorFeature, layerId: string, entry: CatalogueEntry, fallbackColor: string): Feature {
  const geometry = geometryFromGeoJson(vector.geometry) ?? parseWkt(vector.wkt) ?? { kind: "point" as const, point: [0, 0] as LatLngTuple };
  const properties: Record<string, unknown> = { ...(vector.properties ?? {}) };
  const style = styleFromVector(vector.style, fallbackColor);
  const label = style.label?.text ?? propertyText(properties, ["name", "label", "title"]);
  const typeIdName = propertyText(properties, ["typeIdName", "type_IdName"]) ?? (entry.kind === "geoobject" ? entry.source?.typeIdName ?? null : null);
  const cluster: FeatureCluster | null =
    vector.memberCount > 1 ? { count: vector.memberCount, memberIds: vector.memberIds ?? [], typeCounts: { ...(vector.memberTypeCounts ?? {}) } } : null;
  const feature: Feature = {
    key: featureKey(layerId, vector.id),
    id: vector.id,
    layerId,
    geometry,
    anchor: anchorOf(geometry, null),
    properties,
    label,
    style: Object.keys(style).length > 0 ? style : null,
    serverStyle: vector.style ?? null,
    cluster,
    object: null,
    typeIdName
  };

  if (entry.kind === "geoobject") feature.object = mapObjectFromFeature(feature);

  return feature;
}

export function mapObjectFromLocalized(detail: ObjectLocalized, entry: CatalogueEntry | null): MapObject {
  return {
    id: detail.id,
    idName: detail.idName,
    name: detail.name,
    typeId: detail.type_Id ?? 0,
    typeIdName: entry?.source?.typeIdName ?? null,
    typeName: entry?.name ?? null,
    geometryIdName: null,
    parentId: detail.parent_Id,
    showOnMap: detail.showOnMap,
    publishable: detail.publishable,
    lat: detail.lat,
    lon: detail.lon,
    wkt: detail.wkt,
    memberCount: 1,
    memberIds: [detail.id],
    memberTypeCounts: null
  };
}
